<script setup>
import {reactive, onMounted} from 'vue';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import ManageRoleComponent from "@/Components/ManageRoles/ManageRoleComponent.vue";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {ToastUtil} from "@/Utils/ToastUtil";

const compName = 'RolesComponent';

const data = reactive({
  dialog: {
    id: 0,
    status: "closed",
    updateNum: 0,
    create: false,
    update: false,
    view: false
  },
  dataLoading: true,
  roles: [],
  headers: [
    {
      title: 'Name',
      align: 'start',
      sortable: true,
      key: 'role',
    },
    {
      title: 'Default Role',
      sortable: true,
      key: 'is_default'
    },
    { title: 'Actions', key: 'actions', sortable: false, align: 'end' },
  ],
});

const listAll = () => {
  data.loading = true;
  ApiWrapper.get('auth-roles', 'list-roles')
  .then(async (response) => {
    data.roles = response.roles.map((role) => {
      // Add an * by default roles
      return {
        ...role,
        role: role.is_default ? `${role.role}` : role.role
      };
    });
  })
  .catch((e)=>{
    ToastUtil.error(e);
  })
  .finally(() => {
    data.dataLoading = false;
  });
}

const deleteItem = (item) => {
  let deleteMessage = confirm(
      'Are you sure you want to delete this role?'
  )
  if (deleteMessage === true) {
    data.loading = true;
    ApiWrapper.post('auth-roles', 'delete-role', {
          item: {
            id: item.id
          }
        })
        .then((response) => {
          listAll();
          activeComponentStore.notifyComponents(['auth-roles'],compName);
        })
        .catch((e) => {
          ToastUtil.error(e);
        })
        .finally(() => {
          data.loading = false;
        })
  }
}

const viewItem = (item) => {
  data.dialog.id = item.id;
  data.dialog.create = false;
  data.dialog.update = false;
  data.dialog.view = true;
  data.dialog.updateNum++;
  data.dialog.status = "open";
}

const editItem = (item) => {
  data.dialog.id = item.id;
  data.dialog.create = false;
  data.dialog.update = true;
  data.dialog.view = false;
  data.dialog.updateNum++;
  data.dialog.status = "open";
}

const createItem = () => {
  data.dialog.id = 0;
  data.dialog.create = true;
  data.dialog.update = false;
  data.dialog.view = false;
  data.dialog.updateNum++;
  data.dialog.status = "open";
}

onMounted(()=>{
  listAll();
})

</script>

<template>
  <v-container fluid>
    <ManageRoleComponent
        v-show="data.dialog.status === 'open'"
        :roleId="data.dialog.id"
        :status="data.dialog.status"
        :updateNum="data.dialog.updateNum"
        :create="data.dialog.create"
        :update="data.dialog.update"
        :view="data.dialog.view"
        @closed="data.dialog.status='closed'"
        @update="listAll()"
    />

    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Roles</v-toolbar-title>
        <v-progress-linear
            :active="data.dataLoading"
            :indeterminate="data.dataLoading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>

        <v-spacer></v-spacer>

        <!-- New Role Button -->
        <v-btn
            v-if="Permissions.checkPermissions([],true)"
            class="primary"
            size="small"
            @click="createItem()"
        >
          New Role
        </v-btn>
      </v-toolbar>

      <v-data-table
          :headers="data.headers"
          :items="data.roles"
          :loading="data.dataLoading"
          loading-text="Loading... Please wait"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
                {{ item.role }}
            </td>
            <td>
              <v-icon v-if="item.is_default" > mdi-check </v-icon>
            </td>
            <td>
              <span class="float-right">
                <v-icon @click="viewItem(item)"> mdi-eye </v-icon>
                <v-icon v-if="Permissions.checkPermissions([],true)" @click="editItem(item)"> mdi-pencil </v-icon>
                <v-icon v-if="Permissions.checkPermissions([],true)" class="mr-2" @click="deleteItem(item)"> mdi-delete </v-icon>
              </span>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
[v-cloak] {
  display: none;
}
</style>